import { Box, forwardRef, StyleProps } from '@chakra-ui/react';
import { cloneElement, FC, ReactElement } from 'react';

import { createUseVariantStyle } from '@bonusly/ds-hooks';
import { CheckCircleSolid, ExclamationCircleSolid, ExclamationSolid, InformationCircleSolid } from '@bonusly/ds-icon';

import { useAlertContext } from '../context';
import { AlertStatus } from '../types';

const IconMap = new Map<AlertStatus, FC>([
	['info', InformationCircleSolid],
	['success', CheckCircleSolid],
	['warn', ExclamationSolid],
	['warning', ExclamationSolid],
	['error', ExclamationCircleSolid],
]);

const useVariantStyle = createUseVariantStyle({
	size: {
		md: {
			py: 1,
		},
	},
	status: {
		info: { color: 'info.icon' },
		success: { color: 'success.icon' },
		warn: { color: 'warning.icon' },
		warning: { color: 'warning.icon' },
		error: { color: 'error.icon' },
	},
});

const useIcon = () => {
	const { status } = useAlertContext();

	return IconMap.get(status) || InformationCircleSolid;
};

interface AlertIconProps extends StyleProps {
	children?: ReactElement;
	icon?: ReactElement;
}

const AlertIcon = forwardRef<AlertIconProps, 'div'>(({ children, icon, ...rest }, ref) => {
	const { size, status } = useAlertContext();
	const style = useVariantStyle({ size, status });
	const Icon = useIcon();

	return (
		<Box ref={ref} lineHeight={0} {...style} {...rest}>
			{cloneElement(children ?? icon ?? <Icon />, { size: 'md' })}
		</Box>
	);
});

AlertIcon.displayName = 'AlertIcon';

export { AlertIcon };
